import { Link } from 'react-router-dom'
import { getEmails } from '../../api/axios'
import { useState, useEffect } from 'react';
import { day, perc, nf, getBrandClassName } from '../../utilities/Utils'
import useAuth from '../../hooks/useAuth';
import LoadingContainer from '../../components/LoadingContainer';

const EmailsTable = ({ params = {} }) => {

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emails, setEmails] = useState([]);

  const { auth } = useAuth();

  const {
    idBrand,
    idCampaign,
    idJourneyGroup,
    idJourney,
    subject,
    dateFrom,
    dateUntil,
  } = params;

  useEffect(() => {

    const fetchData = async () => {

      setIsLoading(true);

      try {

        const queryConfig = { params: { idBrand, idCampaign, idJourneyGroup, idJourney, subject, dateFrom, dateUntil } };
        const data = await getEmails({ props: queryConfig, auth });

        setEmails(Array.isArray(data.results) ? data.results : []);
        setError(null);

      } catch (fetchError) {

        console.log(fetchError);
        setError(fetchError);
        setEmails([]);

      }

      setIsLoading(false);

    };

    fetchData();

  }, [auth, idBrand, idCampaign, idJourneyGroup, idJourney, subject, dateFrom, dateUntil]);


  const safeEmails = Array.isArray(emails) ? emails : [];
  const hasEmailData = safeEmails.length > 0;

  const toNumber = (value) => Number(value ?? 0);

  const calcPercentDisplay = (total, value) => {
    const totalNumber = toNumber(total);
    const valueNumber = toNumber(value);
    if (totalNumber <= 0 || valueNumber <= 0) {
      return '0%';
    }
    return perc(totalNumber, valueNumber);
  };

  const calcCtorDisplay = (clicks, opened) => {
    const openedNumber = toNumber(opened);
    const clicksNumber = toNumber(clicks);
    if (openedNumber <= 0 || clicksNumber <= 0) {
      return '0%';
    }
    return `${Math.round((clicksNumber / openedNumber) * 100)}%`;
  };

  const getEmailMetrics = (email) => {
    return {
      sent: {
        total: toNumber(email.sent),
        original: toNumber(email.sent_original ?? email.sent_unique),
        resend: toNumber(email.sent_resend),
      },
      opened: {
        total: toNumber(email.opened),
        original: toNumber(email.opened_original),
        resend: toNumber(email.opened_resend),
      },
      unsubscribed: {
        total: toNumber(email.unsubscribed),
        original: toNumber(email.unsubscribed_original),
        resend: toNumber(email.unsubscribed_resend),
      },
      clicks: {
        total: toNumber(email.clicks),
        original: toNumber(email.clicks_original),
        resend: toNumber(email.clicks_resend),
      },
      uniqueContacts: {
        total: toNumber(email.unique_contacts),
        original: toNumber(email.unique_contacts_original),
        resend: toNumber(email.unique_contacts_resend),
      },
    };
  };

  const emailMetricsList = safeEmails.map((email) => ({
    email,
    metrics: getEmailMetrics(email),
  }));

  const totals = emailMetricsList.reduce(
    (acc, { metrics }) => {
      ['sent', 'opened', 'unsubscribed', 'clicks', 'uniqueContacts'].forEach((key) => {
        acc[key].total += metrics[key].total;
        acc[key].original += metrics[key].original;
        acc[key].resend += metrics[key].resend;
      });
      return acc;
    },
    {
      sent: { total: 0, original: 0, resend: 0 },
      opened: { total: 0, original: 0, resend: 0 },
      unsubscribed: { total: 0, original: 0, resend: 0 },
      clicks: { total: 0, original: 0, resend: 0 },
      uniqueContacts: { total: 0, original: 0, resend: 0 },
    }
  );

  const totalsCtorMetrics = {
    total: calcCtorDisplay(totals.clicks.total, totals.opened.total),
    original: calcCtorDisplay(totals.clicks.original, totals.opened.original),
    resend: calcCtorDisplay(totals.clicks.resend, totals.opened.resend),
  };

  const getBrandTextClass = (brandName) => {
    const normalizedBrandName = String(brandName ?? '').trim().toLowerCase();

    if (normalizedBrandName === 'royal canin') {
      return 'emails-table__campaign-name--royal-canin';
    }

    if (normalizedBrandName === 'eukanuba') {
      return 'emails-table__campaign-name--eukanuba';
    }

    if (normalizedBrandName === 'mi vet shop') {
      return 'emails-table__campaign-name--mi-vet-shop';
    }

    return '';
  };

  const summaryCards = [
    {
      label: 'Envíos encontrados',
      value: nf(safeEmails.length),
      detail: hasEmailData ? `${nf(totals.sent.total)} emails enviados` : 'Sin resultados para el filtro actual',
      accent: 'red',
    },
    {
      label: 'Open Rate',
      value: calcPercentDisplay(totals.sent.total, totals.opened.total),
      detail: `${nf(totals.opened.total)} aperturas únicas`,
      accent: 'gold',
    },
    {
      label: 'CTR',
      value: calcPercentDisplay(totals.sent.total, totals.clicks.total),
      detail: `${nf(totals.clicks.total)} clicks únicos`,
      accent: 'ink',
    },
    {
      label: 'CTOR',
      value: totalsCtorMetrics.total,
      detail: `${nf(totals.uniqueContacts.total)} personas alcanzadas`,
      accent: 'sand',
    },
  ];

  const renderMetricCell = ({
    metrics,
    totalExtra = null,
    originalExtra = null,
    resendExtra = null,
    format,
    hasResend = false,
  }) => {
    const formatValue = format ?? ((value) => nf(value));

    if (!hasResend) {
      return (
        <div className="metric-mini-stack metric-mini-stack--single">
          <div className="metric-mini-stack__row metric-mini-stack__row--total">
            <div className="metric-mini-stack__label">Orig</div>
            <div className="metric-mini-stack__value">
              <span className="valor">{formatValue(metrics.total)}</span>
              {totalExtra}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="metric-mini-stack">
        <div className="metric-mini-stack__row">
          <div className="metric-mini-stack__label">Orig</div>
          <div className="metric-mini-stack__value">
            <span className="valor">{formatValue(metrics.original)}</span>
            {originalExtra}
          </div>
        </div>
        <div className="metric-mini-stack__row">
          <div className="metric-mini-stack__label">Rsnd</div>
          <div className="metric-mini-stack__value">
            <span className="valor">{formatValue(metrics.resend)}</span>
            {resendExtra}
          </div>
        </div>
        <div className="metric-mini-stack__row metric-mini-stack__row--total">
          <div className="metric-mini-stack__label">Total</div>
          <div className="metric-mini-stack__value">
            <span className="valor">{formatValue(metrics.total)}</span>
            {totalExtra}
          </div>
        </div>
      </div>
    );
  };

  return (

    <LoadingContainer isLoading={isLoading}>
      <section className="emails-results">
        <section className="emails-summary-shell">
          <div className="emails-summary-shell__intro">
            <span className="emails-eyebrow">Orquestación de envíos</span>
            <h2 className="emails-hero__title">Listado y performance</h2>
          </div>

          <div className="emails-summary-grid">
            {summaryCards.map((card) => (
              <article className={`emails-summary-card emails-summary-card--${card.accent}`} key={card.label}>
                <span className="emails-summary-card__label">{card.label}</span>
                <strong className="emails-summary-card__value">{card.value}</strong>
                <span className="emails-summary-card__detail">{card.detail}</span>
              </article>
            ))}
          </div>
        </section>

        <div className="emails-table-shell emails-results-table-shell">
          {error ? <div className="emails-inline-error">No se pudo cargar el listado de envíos.</div> : null}

          <table className="table table--enhanced emails-table">
            <thead>
              <tr>
                <td>Campaña y asunto</td>
                <td nowrap="nowrap"><span className="emails-table__metric-head">Envíos</span></td>
                <td><span className="emails-table__metric-head">Alcance</span></td>
                <td><span className="emails-table__metric-head">Aperturas</span></td>
                <td><span className="emails-table__metric-head">Clicks</span></td>
                <td><span className="emails-table__metric-head">CTOR</span></td>
                <td><span className="emails-table__metric-head">Desuscriptos</span></td>
              </tr>
            </thead>
            <tbody>
              {!isLoading && !hasEmailData && (
                <tr>
                  <td colSpan="7" className="c loading">No se encontraron resultados</td>
                </tr>
              )}
              {!isLoading && emailMetricsList.map(({ email: item, metrics }) => {
                const hasResend =
                  metrics.sent.resend > 0 ||
                  metrics.opened.resend > 0 ||
                  metrics.unsubscribed.resend > 0 ||
                  metrics.clicks.resend > 0 ||
                  metrics.uniqueContacts.resend > 0 ||
                  !!item.resend_subject;

                const ctorMetrics = {
                  total: calcCtorDisplay(metrics.clicks.total, metrics.opened.total),
                  original: calcCtorDisplay(metrics.clicks.original, metrics.opened.original),
                  resend: calcCtorDisplay(metrics.clicks.resend, metrics.opened.resend),
                };

                return (
                  <tr key={item.id}>
                    <td className="emails-table__campaign-cell">
                      <span className={`emails-table__campaign-name ${getBrandTextClass(item.brand_name)}`.trim()}>{item.campaign_name}</span>
                      <div className="emails-table__email-headline">
                        <Link className="vinculo emails-table__id-link" to={`/emails/edit/${item.id}`}>
                          {item.id}
                        </Link>
                        {item.brand_name ? (
                          <span className={getBrandClassName(item.brand_name, 'table-brand-label')}>
                            {item.brand_name}
                          </span>
                        ) : null}
                        <span className="emails-table__inline-date">{String(item.journey_id) === '0' ? day(item.date_sent_from) : 'Todos los dias'}</span>
                      </div>
                      <div className="metric-line metric-line--subject">
                        <Link className="vinculo metric-value" to={`/emails/${item.id}`}>
                          {item.subject}
                        </Link>
                      </div>
                      {hasResend && item.resend_subject && (
                        <div className="metric-line metric-line--subject">
                          <span className="metric-label">Reenvío</span>
                          <span className="metric-value">{item.resend_subject}</span>
                        </div>
                      )}
                    </td>
                    <td className="c">
                      {renderMetricCell({
                        metrics: metrics.sent,
                        hasResend,
                      })}
                    </td>
                    <td className="c">
                      {renderMetricCell({
                        metrics: metrics.uniqueContacts,
                        hasResend,
                      })}
                    </td>
                    <td className="c">
                      {renderMetricCell({
                        metrics: metrics.opened,
                        hasResend,
                        totalExtra: <span className="metric-extra">{calcPercentDisplay(metrics.sent.total, metrics.opened.total)}</span>,
                        originalExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.original, metrics.opened.original)}</span> : null,
                        resendExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.resend, metrics.opened.resend)}</span> : null,
                      })}
                    </td>
                    <td className="c">
                      {renderMetricCell({
                        metrics: metrics.clicks,
                        hasResend,
                        totalExtra: <span className="metric-extra">{calcPercentDisplay(metrics.sent.total, metrics.clicks.total)}</span>,
                        originalExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.original, metrics.clicks.original)}</span> : null,
                        resendExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.resend, metrics.clicks.resend)}</span> : null,
                      })}
                    </td>
                    <td className="c">
                      {renderMetricCell({
                        metrics: ctorMetrics,
                        hasResend,
                        format: (value) => value,
                      })}
                    </td>
                    <td className="c">
                      {renderMetricCell({
                        metrics: metrics.unsubscribed,
                        hasResend,
                        totalExtra: <span className="metric-extra">{calcPercentDisplay(metrics.sent.total, metrics.unsubscribed.total)}</span>,
                        originalExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.original, metrics.unsubscribed.original)}</span> : null,
                        resendExtra: hasResend ? <span className="metric-extra">{calcPercentDisplay(metrics.sent.resend, metrics.unsubscribed.resend)}</span> : null,
                      })}
                    </td>
                  </tr>
                );
              })}
              {!isLoading && hasEmailData && (
                <tr className="totales emails-table__totals-row">
                  <td colSpan="1" className="r">
                    <span className="valor">Totales</span>
                  </td>
                  <td className="c">
                    {renderMetricCell({ metrics: totals.sent, hasResend: totals.sent.resend > 0 })}
                  </td>
                  <td className="c">
                    {renderMetricCell({ metrics: totals.uniqueContacts, hasResend: totals.uniqueContacts.resend > 0 })}
                  </td>
                  <td className="c">
                    {renderMetricCell({
                      metrics: totals.opened,
                      hasResend: totals.opened.resend > 0,
                      totalExtra: <span className="metric-extra">{calcPercentDisplay(totals.sent.total, totals.opened.total)}</span>,
                      originalExtra: totals.opened.resend > 0 ? <span className="metric-extra">{calcPercentDisplay(totals.sent.original, totals.opened.original)}</span> : null,
                      resendExtra: totals.opened.resend > 0 ? <span className="metric-extra">{calcPercentDisplay(totals.sent.resend, totals.opened.resend)}</span> : null,
                    })}
                  </td>
                  <td className="c">
                    {renderMetricCell({
                      metrics: totals.clicks,
                      hasResend: totals.clicks.resend > 0,
                      totalExtra: <span className="metric-extra">{calcPercentDisplay(totals.sent.total, totals.clicks.total)}</span>,
                      originalExtra: totals.clicks.resend > 0 ? <span className="metric-extra">{calcPercentDisplay(totals.sent.original, totals.clicks.original)}</span> : null,
                      resendExtra: totals.clicks.resend > 0 ? <span className="metric-extra">{calcPercentDisplay(totals.sent.resend, totals.clicks.resend)}</span> : null,
                    })}
                  </td>
                  <td className="c">
                    {renderMetricCell({ metrics: totalsCtorMetrics, format: (value) => value, hasResend: totals.clicks.resend > 0 })}
                  </td>
                  <td className="c">
                    {renderMetricCell({
                      metrics: totals.unsubscribed,
                      hasResend: totals.unsubscribed.resend > 0,
                      totalExtra: <span className="metric-extra">{calcPercentDisplay(totals.sent.total, totals.unsubscribed.total)}</span>,
                      originalExtra: totals.unsubscribed.resend > 0 ? <span className="metric-extra">{calcPercentDisplay(totals.sent.original, totals.unsubscribed.original)}</span> : null,
                      resendExtra: totals.unsubscribed.resend > 0 ? <span className="metric-extra">{calcPercentDisplay(totals.sent.resend, totals.unsubscribed.resend)}</span> : null,
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LoadingContainer>

  )

}

export default EmailsTable
